import Big from 'big.js'

import { STATUS_PILL_TYPES } from '$lib/components/Common/StatusPill/StatusPill_types'
import { messages } from '$lib/messages'
import { EstimateStatus } from '$lib/stores/estimate/types'
// import { EstimateStatus } from '$lib/stores/estimates/types'
import type { Currency, DocListItem } from '$lib/types/client_types'

import { InvoiceStatus } from '../types'

export const PHONE_MAX_LENGTH = 20

const DOC_STATUSES_TYPE = {
	[InvoiceStatus.Loading]: STATUS_PILL_TYPES.DEFAULT,
	[InvoiceStatus.Draft]: STATUS_PILL_TYPES.DEFAULT,
	[InvoiceStatus.Sent]: STATUS_PILL_TYPES.NOTICE,
	[InvoiceStatus.Viewed]: STATUS_PILL_TYPES.SUCCESS,
	[InvoiceStatus.PartialPaid]: STATUS_PILL_TYPES.WARNING,
	[InvoiceStatus.Paid]: STATUS_PILL_TYPES.SUCCESS,
	[InvoiceStatus.Refunded]: STATUS_PILL_TYPES.DEFAULT,
	[InvoiceStatus.Deleted]: STATUS_PILL_TYPES.ERROR,

	[EstimateStatus.Approved]: STATUS_PILL_TYPES.SUCCESS,
	[EstimateStatus.Invoiced]: STATUS_PILL_TYPES.SUCCESS,
	[EstimateStatus.Error]: STATUS_PILL_TYPES.ERROR
}
export function getDocStatusType(status: InvoiceStatus | EstimateStatus) {
	return DOC_STATUSES_TYPE[status]
}

const DOC_STATUSES_TEXT = {
	[InvoiceStatus.Loading]: '',
	[InvoiceStatus.Draft]: 'Created',
	[InvoiceStatus.Sent]: 'Sent',
	[InvoiceStatus.Viewed]: 'Viewed',
	[InvoiceStatus.PartialPaid]: 'Partial Paid',
	[InvoiceStatus.Paid]: ' Paid',
	[InvoiceStatus.Refunded]: 'Refunded',
	[InvoiceStatus.Deleted]: 'Deleted',

	[EstimateStatus.Approved]: 'Approved',
	[EstimateStatus.Invoiced]: 'Invoiced',
	[EstimateStatus.Error]: 'Error'
}
export function formatDocStatus(status: InvoiceStatus | EstimateStatus) {
	return DOC_STATUSES_TEXT[status]
}

export const getDocStatusTimeSince = (doc: DocListItem): number => {
	// console.log('doc.status', doc.status)
	if (doc.status === undefined) return 0
	else if (doc.status === InvoiceStatus.Loading) return 0
	else if (doc.status === InvoiceStatus.Draft) return doc.created_at || 0
	else if (doc.status === InvoiceStatus.Sent) return doc.last_sent_at || 0
	else if (doc.status === InvoiceStatus.Viewed) return doc.last_viewed_at || 0
	else if (doc.status === InvoiceStatus.PartialPaid) return doc.last_paid_at || 0
	else if (doc.status === InvoiceStatus.Paid) return doc.last_paid_at || 0
	else if (doc.status === InvoiceStatus.Refunded) return doc.last_paid_at || 0
	else if (doc.status === InvoiceStatus.Deleted) return doc.last_deleted_at || 0
	else if (doc.status === EstimateStatus.Approved) return doc.approved_at || 0
	else if (doc.status === EstimateStatus.Invoiced) return doc.invoiced_at || 0
	else if (doc.status === EstimateStatus.Error) return 0
	else throw new Error('Unexpected doc status ' + doc.status)
}

const MONTHS = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December'
]

/**
 *
 * Returns a simple date like March 11, 2010
 *
 */
export function formatDate(value: string) {
	const [year, m, day] = value.split('-').map((chunk) => parseInt(chunk))
	const month = MONTHS[m - 1]
	return `${month} ${day}, ${year}`
}

/**
 * Returns a representation of a percent (5.25 = 5.25%, not 0.525), assumes a Big decimal is passed in.
 */
export function formatRate(value: Big, decimals = 2) {
	return value.toFixed(decimals) + '%'
}

function addThousandsSeparator(n: string, separator: string) {
	return n.replace(/\B(?=(\d{3})+(?!\d))/g, separator)
}

/**
 * Returns a financial representation of a Big
 */
// export function formatMoney(value: Big, symbol = '$', separator = ',') {
export function formatMoney(value: Big, symbol = '', separator = ',') {
	if (value.lt(0)) {
		return '-' + symbol + addThousandsSeparator(value.abs().toFixed(2), separator)
	} else {
		return symbol + addThousandsSeparator(value.toFixed(2), separator)
	}
}

export function formatMoneyPrefixLong(currency_id: string, currency_symbol: string) {
	let prefix = ''
	if (currency_id.toUpperCase() == 'USD') prefix = currency_symbol ? ' ' + currency_symbol : ''
	else {
		if (currency_id.toUpperCase() != currency_symbol.toLocaleUpperCase()) {
			prefix = currency_id.toUpperCase() + (currency_symbol ? ' ' + currency_symbol : ' ')
		} else prefix = currency_id.toUpperCase() + ' '
	}
	return prefix
}
/**
 * Returns a financial representation of a Big
 */
export function formatMoneyShort(currency: Currency, value?: Big, separator = ',') {
	let prefix = ''
	if (currency) prefix = currency.symbol ? ' ' + currency.symbol : ''

	if (value) {
		if (value.lt(0)) {
			return '-' + prefix + addThousandsSeparator(value.abs().toFixed(2), separator)
		} else {
			return prefix + addThousandsSeparator(value.toFixed(2), separator)
		}
	}
	return prefix + '0'
}

/**
 * Returns a financial representation of a Big
 */
export function formatMoneyLong(currency: Currency, value: Big, separator = ',') {
	if (!currency) return
	// console.log('formatMoneyLong', currency, currency.id)
	let prefix = ''
	if (currency) {
		prefix = formatMoneyPrefixLong(currency.id, currency.symbol)
	}

	let result = ''
	if (value.lt(0)) {
		result = '-' + prefix + addThousandsSeparator(value.abs().toFixed(2), separator)
	} else {
		result = prefix + addThousandsSeparator(value.toFixed(2), separator)
	}
	// console.log('formatMoneyLong', currency.id, result)
	return result
}
/**
 * Returns a formatted quantity representation of a Big
 */
export function formatQuantity(value: Big) {
	return value.toString()
}

export enum SaveStatus {
	Error = 'error',
	Draft = 'draft',
	Saving = 'saving',
	Saved = 'saved'
}

const SAVE_STATUSES = {
	[SaveStatus.Error]: 'Error',
	[SaveStatus.Draft]: 'Draft',
	[SaveStatus.Saving]: 'Saving',
	[SaveStatus.Saved]: 'Saved'
}

/**
 * Returns human readable status
 */
export function formatSaveStatus(status: SaveStatus) {
	return SAVE_STATUSES[status]
}

export type Company = {
	address_1: string
	address_2: string
	city: string
	state_region: string
	postal_code: string
}

/**
 * Returns a single line address
 */
export function formatSingleLineAddress(company: Company) {
	const fields = [
		company.address_1,
		company.address_2,
		company.city,
		company.state_region,
		company.postal_code
	]
	return fields.filter((field) => !!field).join(', ')
}

export const getZipName = (country: string): string => {
	return ['CA', 'Canada', 'Canadá'].includes(country) ? 'Postal Code' : 'Zip'
}

export const capitalizeFirst = (s: string): string => {
	return s.charAt(0).toUpperCase() + s.slice(1)
}


export function isValidEmail(email: string): boolean {
	// const emailRegex = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
	const emailRegex = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
	return emailRegex.test(email)
}	

export const isNumeric = (value: string): boolean => {
	return /^-?\d+$/.test(value)
}

export const cmpSort = (a: any, b: any, type?: string) => {
	const mult = (type || 'asc') === 'desc' ? -1 : 1

	if (a instanceof Big) {
		return a.cmp(b) * mult
	}

	if (a > b) return 1 * mult

	if (a < b) return -1 * mult

	return 0
}

export function checkPhoneLen(value: string) {
	if (value.length === PHONE_MAX_LENGTH)
		messages.warning(
			`The maximum length allowed for the phone number is ${PHONE_MAX_LENGTH} characters.`
		)
}
